/**
 * TanStack Query hooks for trips. Replaces server-state logic previously in TripContext.
 *
 * Benefits: list and single-trip fetches are deduped by query key (no duplicate requests in Strict Mode);
 * mutations invalidate the list/trip cache so the UI refetches fresh data; loading and error state come
 * from the query/mutation instead of manual context state.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services';
import { unwrapServiceResultAsync } from '../services/utils/errorUtils';
import { logger } from '../utils/logger';
import type { TripDetail, TripRequest } from '../types';
import type { ApiResponse } from '../types/common/api.types';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../constants';

/** Query keys: same key = shared request and cache. */
export const TRIPS_QUERY_KEY = ['trips'] as const;
export const tripListQueryKey = (page: number, size: number) =>
  [...TRIPS_QUERY_KEY, page, size] as const;
export const TRIP_QUERY_KEY = ['trip'] as const;
export const tripByIdQueryKey = (id: string) =>
  [...TRIP_QUERY_KEY, id] as const;
export const sharedTripQueryKey = (token: string) =>
  ['trip', 'shared', token] as const;

/**
 * Paginated trips list query.
 *
 * Flow:
 * 1. When enabled (default), TanStack Query runs queryFn and caches the result by queryKey.
 * 2. queryKey is ['trips', page, size] — same key = shared cache; different page/size = separate entries.
 * 3. Components (e.g. TripList) get data, isLoading, isError, refetch from the returned query object.
 * 4. When another mutation invalidates TRIPS_QUERY_KEY, this query refetches so the list stays in sync.
 *
 * Set options.enabled: false when not authenticated to avoid firing a 401 (e.g. on login page).
 */
export function useTripsQuery(
  page = DEFAULT_PAGE_NUMBER,
  size = DEFAULT_PAGE_SIZE,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: tripListQueryKey(page, size),
    queryFn: () =>
      unwrapServiceResultAsync(
        apiService.trips.getCurrentUserTrips(page, size)
      ),
    enabled: options?.enabled !== false,
  });
}

/**
 * Single trip by id query.
 *
 * Flow:
 * 1. When id is defined and enabled, the query fetches one trip; cache key is ['trip', id].
 * 2. Used on trip detail/edit screens; same id = shared cache across components.
 * 3. When id is undefined (e.g. create form), enabled: false so no request is sent.
 * 4. After update/delete, invalidating this key (or the list key) triggers refetch as needed.
 */
export function useTripByIdQuery(id: string | undefined) {
  return useQuery({
    queryKey: tripByIdQueryKey(id!),
    queryFn: () => unwrapServiceResultAsync(apiService.trips.getTripById(id!)),
    enabled: !!id,
  });
}

/**
 * Shared trip by token query.
 *
 * Flow:
 * 1. When shareToken is defined and enabled, the query fetches one shared trip; cache key is ['trip', 'shared', token].
 * 2. Used on shared trip view screens; same token = shared cache across components.
 * 3. When shareToken is undefined (e.g. not a shared trip), enabled: false so no request is sent.
 * 4. After update/delete, invalidating this key (or the list key) triggers refetch as needed.
 */
export function useSharedTripQuery(shareToken: string | undefined) {
  return useQuery({
    queryKey: sharedTripQueryKey(shareToken!),
    queryFn: () =>
      unwrapServiceResultAsync(apiService.trips.getSharedTrip(shareToken!)),
    enabled: !!shareToken,
  });
}

/**
 * Create trip mutation.
 *
 * Flow:
 * 1. Caller invokes mutate(tripData) or mutateAsync(tripData); TanStack Query runs mutationFn.
 * 2. mutationFn calls the API (createTrip), unwraps the result; response includes id from Location header.
 * 3. On success, onSuccess runs: we log the created trip id and invalidate the trips list cache.
 * 4. Invalidation marks ['trips'] queries stale, so TripList refetches and the new trip appears.
 *
 * The returned mutation object provides: mutate(tripData), mutateAsync(tripData), isPending, isError, etc.
 */
export function useCreateTripMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tripData: TripRequest) =>
      unwrapServiceResultAsync(
        apiService.trips.createTrip(tripData)
      ) as Promise<ApiResponse>,
    onSuccess: () => {
      logger.info('Trip created');
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
    },
  });
}

/**
 * Replace trip mutation.
 *
 * Flow:
 * 1. Caller invokes mutate({ id, tripData }) or mutateAsync({ id, tripData }); mutationFn runs.
 * 2. mutationFn calls the API (replaceTrip), unwraps the result (returns updated Trip).
 * 3. On success, onSuccess runs: we log the trip id, invalidate the list and this trip's cache.
 * 4. Invalidating ['trips'] and ['trip', id] causes TripList and any useTripByIdQuery(id) to refetch.
 *
 * The returned mutation object provides: mutate({ id, tripData }), mutateAsync(...), isPending, etc.
 */
export function useReplaceTripMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tripData }: { id: string; tripData: TripRequest }) =>
      unwrapServiceResultAsync(
        apiService.trips.replaceTrip(id, tripData)
      ) as Promise<TripDetail>,
    onSuccess: (_, { id }) => {
      logger.info('Trip replaced', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(id) });
    },
  });
}

/**
 * Partial update trip mutation.
 *
 * Flow:
 * 1. Caller invokes mutate({ id, tripData }) or mutateAsync({ id, tripData }); mutationFn runs.
 * 2. mutationFn calls the API (partialUpdateTrip), unwraps the result (returns updated Trip).
 * 3. On success, onSuccess runs: we log the trip id, invalidate the list and this trip's cache.
 * 4. Invalidating ['trips'] and ['trip', id] causes TripList and any useTripByIdQuery(id) to refetch.
 *
 * The returned mutation object provides: mutate({ id, tripData }), mutateAsync(...), isPending, etc.
 */
export function usePartialUpdateTripMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      tripData,
    }: {
      id: string;
      tripData: Partial<TripRequest>;
    }) =>
      unwrapServiceResultAsync(
        apiService.trips.updateTrip(id, tripData)
      ) as Promise<TripDetail>,
    onSuccess: (_, { id }) => {
      logger.info('Trip updated', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(id) });
    },
  });
}

/**
 * Enable share link mutation.
 *
 * Flow:
 * 1. Caller invokes mutate(id) or mutateAsync(id); mutationFn runs.
 * 2. mutationFn calls the API (enableShareLink), unwraps the result (returns updated Trip).
 * 3. On success, onSuccess runs: we log the trip id, invalidate the list and this trip's cache.
 * 4. Invalidating ['trips'] and ['trip', id] causes TripList and any useTripByIdQuery(id) to refetch.
 *
 * The returned mutation object provides: mutate(id), mutateAsync(...), isPending, etc.
 */
export function useEnableShareLinkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrapServiceResultAsync(
        apiService.trips.enableShareLink(id)
      ) as Promise<TripDetail>,
    onSuccess: (_, id) => {
      logger.info('Share link enabled', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(id) });
    },
  });
}

/**
 * Revoke share link mutation.
 *
 * Flow:
 * 1. Caller invokes mutate(id) or mutateAsync(id); mutationFn runs.
 * 2. mutationFn calls the API (revokeShareLink), unwraps the result (returns updated Trip).
 * 3. On success, onSuccess runs: we log the trip id, invalidate the list and this trip's cache.
 * 4. Invalidating ['trips'] and ['trip', id] causes TripList and any useTripByIdQuery(id) to refetch.
 *
 * The returned mutation object provides: mutate(id), mutateAsync(...), isPending, etc.
 */
export function useRevokeShareLinkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrapServiceResultAsync(
        apiService.trips.revokeShareLink(id)
      ) as Promise<TripDetail>,
    onSuccess: (_, id) => {
      logger.info('Share link revoked', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(id) });
    },
  });
}

/**
 * Rotate share link mutation.
 *
 * Flow:
 * 1. Caller invokes mutate(id) or mutateAsync(id); mutationFn runs.
 * 2. mutationFn calls the API (rotateShareLink), unwraps the result (returns updated Trip).
 * 3. On success, onSuccess runs: we log the trip id, invalidate the list and this trip's cache.
 * 4. Invalidating ['trips'] and ['trip', id] causes TripList and any useTripByIdQuery(id) to refetch.
 *
 * The returned mutation object provides: mutate(id), mutateAsync(...), isPending, etc.
 */
export function useRotateShareLinkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrapServiceResultAsync(
        apiService.trips.rotateShareLink(id)
      ) as Promise<TripDetail>,
    onSuccess: (_, id) => {
      logger.info('Share link rotated', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: tripByIdQueryKey(id) });
    },
  });
}

/**
 * Delete trip mutation.
 *
 * Flow:
 * 1. Caller invokes mutate(id) or mutateAsync(id); TanStack Query runs mutationFn(id).
 * 2. mutationFn calls the API (deleteTrip), unwraps the service result (throws on non-2xx).
 * 3. On success, onSuccess runs: we log the deleted id and invalidate the trips cache.
 * 4. Invalidation marks all queries whose key starts with ['trips'] as stale, so any
 *    active useTripsQuery (e.g. TripList) refetches and the deleted trip disappears.
 *
 * The returned mutation object provides: mutate(id), mutateAsync(id), isPending, isError,
 * error, etc. Components use mutate() for fire-and-forget or mutateAsync() for await + try/catch.
 */
export function useDeleteTripMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrapServiceResultAsync(
        apiService.trips.deleteTrip(id)
      ) as Promise<ApiResponse>,
    onSuccess: (_data, id) => {
      logger.info('Trip deleted', id);
      queryClient.invalidateQueries({ queryKey: TRIPS_QUERY_KEY });
    },
  });
}
